/**
 * ConversationDetailSheet — one Conversations row, opened (TRA-1412).
 *
 * A wide sheet from the right over the conversations list, the same chrome
 * as `RequestDetailSheet`. The transcript is the Conversation page's own
 * `ConversationTranscript`, which brings its loading and error states; this
 * file owns only the sheet, the id line and the link to the full page.
 * `ConversationDetailBody` is what the sheet holds, kept apart so it can be
 * rendered in tests, where the sheet's portal does not mount.
 */

import { Link } from 'react-router-dom'

import { MONO } from '@/components/models/home/RequestDetailContent'
import { ConversationTranscript } from '@/components/traces/ConversationTranscript'
import {
  conversationPath,
  useConversationLinkState,
} from '@/components/traces/conversationLink'
import { CopyButton } from '@/components/ui/copy-button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'

interface ConversationDetailBodyProps {
  conversationId: string
  /** The page this sheet is on, for the full page's back arrow. */
  pageName: string
}

export function ConversationDetailBody({
  conversationId,
  pageName,
}: ConversationDetailBodyProps): React.JSX.Element {
  const linkState = useConversationLinkState(pageName)
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className={MONO}>{conversationId}</span>
        <CopyButton value={conversationId} label="Copy conversation id" />
        <Link
          to={conversationPath(conversationId)}
          state={linkState}
          className="text-foreground underline underline-offset-2"
        >
          Open full page
        </Link>
      </div>
      <ConversationTranscript conversationId={conversationId} />
    </div>
  )
}

interface ConversationDetailSheetProps {
  /** The open row's conversation id; null keeps the sheet closed. */
  conversationId: string | null
  pageName: string
  onClose: () => void
}

export function ConversationDetailSheet({
  conversationId,
  pageName,
  onClose,
}: ConversationDetailSheetProps): React.JSX.Element {
  return (
    <Sheet open={conversationId !== null} onOpenChange={(open) => { if (!open) onClose() }}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-3xl">
        <SheetHeader className="mb-6">
          <SheetTitle>Conversation</SheetTitle>
          <SheetDescription>See each turn of this run.</SheetDescription>
        </SheetHeader>
        {conversationId === null ? null : (
          // Remount per conversation: the transcript's search and
          // disclosure state would otherwise carry over.
          <ConversationDetailBody
            key={conversationId}
            conversationId={conversationId}
            pageName={pageName}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}
